import React, { useEffect, useState } from "react";
import HeaderSection from "../../components/HeaderSection";
import {
  CheckCircleIcon,
  EyeIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  ShoppingCartIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon
} from "@heroicons/react/24/outline";
import SearchBar from "../../components/searchbar";
import SiteFormModel from "./SitesFormModal";
import { useAppDispatch, useAppSelector } from "../../store";
import { useNavigate } from "react-router-dom";
import {
  selectSiteError,
  selectSiteLoading,
  selectSites,
  fetchSites,
  createSite,
  removeSite,
  editSite,
  selectSitePagination,
} from "./siteSlice";
import { Site } from "./site";
// Removed CreateOrderPage import since we're now navigating to a new page
import { usePermission } from "../../components/common/PermissionGuard";
import dayjs from "dayjs";
import ConfirmDialog from "../../components/common/ConfirmDialogue";
import { clearSelectedOrder, fetchOrders, selectOrders } from "../orders/orderSlice";
import PageTransition from "../../components/common/PageTransition";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import UnifiedTimelineModal from "../../components/timeline/UnifiedTimelineModal";
import { UnifiedTimelineData } from "../../types/timeline";
import customerTimelineService from "../../services/customerTimelineService";
import { Timeline as TimelineIcon } from "@mui/icons-material";
import PageWrapper from "../../components/common/PageWrapper";
import Pagination from "../../components/common/Pagination";
import { Divider, Typography, Box, Card, CardContent, IconButton, Chip } from "@mui/material";
import CloseConfirmationDialogue from "../../components/common/__tests__/CloseConfirmationDialogue";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { ItemStageSidePanel, OrderSidePanel, ProductSidePanel } from "../../components/common";
import { Order } from "../orders/order";
import { orderService } from "../orders/orderService";
import { useSidePanel } from "../../hooks/useSidePanel";
import { capitalizeWords } from "@/utils/formatters";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Sites = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const pagination = useAppSelector(selectSitePagination);
  const sites = useAppSelector(selectSites) || [];
  const loading = useAppSelector(selectSiteLoading);
  const error = useAppSelector(selectSiteError);
  
  // Permission checks
  const canEditSite = usePermission('sites', 'update');
  const canCreateOrder = usePermission('orders', 'create');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedSite, setSelectedSite] = useState<Site | any>(null);
  // const [selectedSiteId, setSelectedSiteId] = useState<Site | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  // Removed orderModelOpen since we're now navigating to a new page
  const [DeleteDialog, setDeleteDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [deleteSiteId, setDeleteSiteId] = useState<string | null>(null);
  const [newSiteId, setNewSiteId] = useState<string | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [timelineData, setTimelineData] = useState<UnifiedTimelineData | null>(
    null
  );
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [creatingOrderForSite, setCreatingOrderForSite] = useState<string | null>(null);
  
  // Side panel management
  const {
    // State
    showOrderSidePanel,
    showProductSidePanel,
    showItemSidePanel,
    selectedSiteForOrders,
    selectedOrderForProducts,
    selectedProduct,
    selectedOrder,
    siteOrders,
    orderProducts,
    ordersLoading,
    productsLoading,
    
    // Close functions
    closeAll,
    closeOrderPanel,
    closeProductPanel,
    closeItemPanel,
    
    // Back navigation functions
    backToSitesFromOrders,
    backToOrdersFromProducts,
    backToProductsFromItems,
    
    // Open functions
    openOrderPanel,
    openProductPanel,
    openItemPanelFromProducts,
    
    // Loading functions
    setOrdersLoadingState,
    setProductsLoadingState,
  } = useSidePanel();

  const canEdit = usePermission("sites", "update");
  const canDelete = usePermission("sites", "delete");

  useEffect(()=>{
    console.log("selectedSiteForOrders from site", selectedSiteForOrders);
    console.log("sites====>", sites);
  },[selectedSiteForOrders,sites])

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Fetch sites with debounced search term
  useEffect(() => {
    dispatch(fetchSites({
      page: pagination?.page || DEFAULT_PAGINATION.page,
      limit: pagination?.limit || DEFAULT_PAGINATION.limit,
      search: debouncedSearchTerm,
      // status: filterType === "all" ? "" : filterType, // if you use status
    }));
  }, [dispatch, pagination?.page, pagination?.limit, debouncedSearchTerm]);

  const handleCloseCreateModal = () => {
    setCloseDialog(true);
    setIsCreateModalOpen(false);
    setSelectedSite(null);
  };

  const handleOrderCreated = (order: any) => {
    navigate(`/orders/${order.id}`);
  };

  const handleEditSite = (site: Site | any) => {
    console.log("Selected site for order:", site);
    setSelectedSite(site);
    setIsCreateModalOpen(true);
  };

  const showCreateOrderPopup = async (site: any) => {
    // Prevent multiple clicks while creating order for this specific site
    if (creatingOrderForSite === site.id) {
      console.log("🚫 Order creation already in progress for site:", site.id);
      return;
    }

    console.log("🚀 Creating order for site:", site);
    
    // Set loading state for this specific site
    setCreatingOrderForSite(site.id);
    
    try {
      // Navigate to order creation page with customer ID and site ID
      const customerId = site.customerId || site.customer?.customerId || site.customer?.id;
      
      if (customerId) {
        console.log("🚀 Navigating to order creation with customer and site:", {
          customerId,
          siteId: site.id,
          customerName: site.customer?.name || site.customer?.customerName,
          siteName: site.name
        });
        
        navigate(`/orders/enhanced/${customerId}/${site.id}`);
      } else {
        console.error("❌ No customer ID found for site:", site);
        // Fallback: navigate to order creation page with just site ID
        navigate(`/orders/enhanced/undefined/${site.id}`);
      }
    } catch (error) {
      console.error("❌ Error navigating to order creation:", error);
    } finally {
      // Reset loading state after navigation
      setCreatingOrderForSite(null);
    }
  };

  useEffect(() => {
    console.log("::::::Selected site from site::::::::::", selectedSite);
    console.log(
      "::::::Selected site from site siteId::::::::::",
      selectedSite?.id
    );
    console.log(
      "::::::Selected site from site customerId::::::::::",
      selectedSite?.customer?.customerId
    );
    console.log(
      "::::::Selected site customerId field::::::::::",
      selectedSite?.customerId
    );
  }, [selectedSite, setSelectedSite]);

  // Removed useEffect for orderModelOpen since we're no longer using modals

  // Since we're doing server-side search, no need for client-side filtering
  // Just use the sites directly from the API
  const filteredSites = sites || [];
  console.log("filteredSites: ",filteredSites)

  const handleSiteCreated = (site: Site) => {
    console.log("Newly created site ID in handleSiteCreated:", site);
    // Close the create modal
    setIsCreateModalOpen(false);
    // Set the selected site first
    setSelectedSite(null);
    // Refresh the sites list to show the newly created site
    dispatch(fetchSites({
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearchTerm,
    }));
    // Open confirmation dialog for creating order
    // setOpenConfirmDialog(true);
  };

  const handleDeleteSite = (site: Site) => {
    dispatch(removeSite(site.id));
  };

  const handlePageChange = (page: number) => {
      dispatch(
        fetchSites({
          page,
          limit: pagination.limit,
          search: debouncedSearchTerm,
          // status: filterType === "all" ? "" : filterType,
        })
      );
    };
  
    const handleItemsPerPageChange = (itemsPerPage: number) => {
      dispatch(
        fetchSites({
          page: 1, // Reset to first page when changing items per page
          limit: itemsPerPage,
          search: debouncedSearchTerm,
          // status: filterType === "all" ? "" : filterType,
        })
      );
    };

  const handleTimelineView = async (site: Site) => {
    setTimelineLoading(true);
    try {
      // Fetch dynamic data from services
      const timelineSiteData =
        await customerTimelineService.getSiteWithTimelineData(site.id);
      console.log("Fetched timeline data for site:", timelineSiteData);
      setTimelineData({
        type: "site",
        site: timelineSiteData,
      });
      setShowTimelineModal(true);
    } catch (error) {
      console.error("Failed to load timeline data:", error);
      // Show error message to user instead of fallback
      alert("Failed to load timeline data. Please try again later.");
    } finally {
      setTimelineLoading(false);
    }
  };


  // Handle product selection for side panel
  const handleProductSelect = async (order: Order, product: any) => {
    console.log('Sites - handleProductSelect called with:', { order, product });
    
    try {
      // Fetch full order data with products and items
      console.log('Sites - Fetching order data for ID:', order.id);
      const response = await orderService.getOrder(order.id);
      console.log('Sites - Order service response:', response);
      
      if (response.success) {
        const fullOrder = response.data;
        console.log('Sites - Full order data:', fullOrder);
        console.log('Sites - Full order products:', fullOrder.products);
        
        // Find the matching product in the full order data
        const matchingProduct = fullOrder.products?.find(p => p.id === product.id) || fullOrder.products?.[0];
        console.log('Sites - Matching product:', matchingProduct);
        
        if (matchingProduct) {
          openItemPanelFromProducts(matchingProduct, fullOrder);
        } else {
          console.error('Product not found in full order data');
          // Fallback to the original product
          openItemPanelFromProducts(product, order);
        }
      } else {
        console.error('Failed to fetch full order data:', response.error);
        // Fallback to the original product
        openItemPanelFromProducts(product, order);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      // Fallback to the original product
      openItemPanelFromProducts(product, order);
    }
  };

  // Handle item selection in side panel
  const handleItemSelect = (item: any) => {
    console.log('Item selected:', item);
    // Additional logic for item selection can be added here
  };

  // Handle stage selection in side panel
  const handleStageSelect = (item: any, stage: any) => {
    console.log('Stage selected:', item, stage);
    // Additional logic for stage selection can be added here
  };

  // New handlers for site → orders → products flow
  const handleSiteSelect = async (site: Site) => {
    console.log('Sites - handleSiteSelect called with:', site);
    console.log('Sites - Setting showOrderSidePanel to true');
    setOrdersLoadingState(true);
    
    try {
      console.log('Fetching orders for site ID:', site.id);
      const response = await orderService.getOrders({ siteId: site.id });
      console.log('Orders response:', response);
      
      if (response.success) {
        const ordersData = response.data;
        const orders = ordersData?.orders || [];
        console.log('Orders for site:', orders.length, orders);
        openOrderPanel(site, orders);
      } else {
        console.error('Failed to fetch orders:', response.error);
        openOrderPanel(site, []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      openOrderPanel(site, []);
    } finally {
      setOrdersLoadingState(false);
    }
  };

  const handleOrderSelect = async (order: any) => {
    console.log('Order selected:', order);
    setProductsLoadingState(true);
    
    try {
      console.log('Fetching full order details for:', order.id);
      const response = await orderService.getOrder(order.id);
      console.log('Order details response:', response);
      
      if (response.success) {
        const fullOrder = response.data;
        console.log('Full order data:', fullOrder);
        
        if (fullOrder.products && fullOrder.products.length > 0) {
          console.log('Order products found:', fullOrder.products.length, fullOrder.products);
          openProductPanel(order, fullOrder.products);
        } else {
          console.log('No products found in order, using empty array');
          openProductPanel(order, []);
        }
      } else {
        console.error('Failed to fetch order details:', response.error);
        openProductPanel(order, []);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      openProductPanel(order, []);
    } finally {
      setProductsLoadingState(false);
    }
  };

  const handleProductSelectFromPanel = (product: any) => {
    console.log('Product selected from panel:', product);
    openItemPanelFromProducts(product, selectedOrderForProducts);
  };

  // Back navigation handlers
  const handleBackToSites = () => {
    console.log('Back to sites clicked');
    backToSitesFromOrders();
  };

  const handleBackToOrders = () => {
    console.log('Back to orders clicked');
    backToOrdersFromProducts();
  };

  const handleBackToProducts = () => {
    console.log('Back to products clicked');
    backToProductsFromItems();
  };

  return (
    <>
    <PageWrapper >
      <HeaderSection
        title="Sites"
        description="Manage your sites location and their information"
        icon={
          <MapPinIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        }
        buttonIcon={<PlusIcon className="h-5 w-5 mr-2" />}
        buttonText="Add Sites"
        onButtonClick={() => setIsCreateModalOpen(true)}
      />

      <div className="flex-1 overflow-auto">
        <div className="mb-6 flex justify-between items-center">
          <div className="w-64 flex-1">
            <SearchBar
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {/* <div className="flex mb-4 ml-3">
            <select
              className="block w-48 pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={selectedCity || ""}
              onChange={(e) => setSelectedCity(e.target.value || null)}
            >
              <option value="">All Cities</option>
              {cityOptions.map((city) => (
              <option key={city} value={city}>
                  {city}
              </option>
              ))}
            </select>
          </div> */}
        </div>

        {/* <div className="bg-white dark:bg-gray-800 bw:bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bw:divide-black">
              <thead className="bg-gray-50 dark:bg-gray-700 bw:bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Site Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Contact Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Contact Number
                  </th>
                  
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Total Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Completed Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Pending Orders
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 bw:text-black uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bw:divide-black">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 bw:text-black"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredSites?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 bw:text-black"
                    >
                      No sites found
                    </td>
                  </tr>
                ) : (
                  filteredSites.map((site: any) => (
                    <tr key={site.id} onClick={() => handleSiteSelect(site)}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white bw:text-black cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 bw:hover:text-blue-800">
                          {site.name}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {site.address}
                          </Typography>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.customer?.name || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.contactPerson || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.contactPhone || "N/A"}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.totalOrder ? site.totalOrder : "0"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.completedOrder ? site.completedOrder : "0"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white bw:text-black">
                          {site.pendingOrder ? site.pendingOrder : "0"}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              showCreateOrderPopup(site);
                            }}
                            disabled={creatingOrderForSite === site.id || loading}
                            className={`${
                              creatingOrderForSite === site.id || loading
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-blue-600 dark:text-blue-400 bw:text-black hover:text-blue-900 dark:hover:text-blue-300 bw:hover:text-gray-700"
                            }`}
                            title={creatingOrderForSite === site.id ? "Creating Order..." : "Create Order"}
                          >
                            <ShoppingCartIcon className="h-5 w-5" />
                          </button>
                          
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); 
                                handleEditSite(site);
                              }}
                              className="text-indigo-600 dark:text-indigo-400 bw:text-black hover:text-indigo-900 dark:hover:text-indigo-300 bw:hover:text-gray-700"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div> */}


        {loading && filteredSites.length === 0 ? (
          // Show loading state only when there are no sites to display
          <div className="p-6 text-center rounded-xl shadow-md bg-white dark:bg-secondary-800">
            <div className="flex flex-col items-center justify-center min-h-[300px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <h2 className="text-lg font-semibold">Loading Sites...</h2>
            </div>
          </div>
        ) : filteredSites.length === 0 ? (
          // Show empty state when not loading and no sites
          <div className="p-6 text-center rounded-xl shadow-md bg-white dark:bg-secondary-800">
            <h2 className="text-lg font-semibold mb-2">
              No sites found
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm ? `No sites match "${searchTerm}"` : "There are no sites available"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => dispatch(fetchSites({}) as any)}
                className="mt-2 px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-md hover:shadow-lg transition"
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <div className="relative">
            {/* Loading overlay when fetching with existing data */}
            {loading && (
              <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span className="text-sm font-medium">Searching...</span>
                </div>
              </div>
            )}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSites.map((site) => (
                  
                  <div
                    key={`${site.id}-${site.status}`}
                    className="flex flex-col justify-between hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-gray-900 rounded-2xl shadow-md pt-2 h-full"
                    onClick={() => handleSiteSelect(site)}
                  >
                  
  
                    <div className="flex-1 flex flex-col justify-start ml-1 pr-4 pl-4">
                      <div className="flex items-center justify-between mb-4">
                        {/* Left: Avatar + Name */}
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                            <BuildingOfficeIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="gap-0 overflow-hidden">
                            <h3
                              className="text-lg font-semibold text-gray-900 dark:text-white truncate"
                              // title={site.name} // ← shows full name on hover
                            >
                              {capitalizeWords(site.name) || ""}
                            </h3>
                            <Typography variant="caption" color="text.secondary">
                              {site.namePlate || 'N/A'}
                            </Typography>

                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <div className="space-y-3">
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <UserCircleIcon className="h-5 w-5 mr-2" />
                            <span className="truncate max-w-[140px]" title={capitalizeWords(site.customer?.name) || "N/A"}>{capitalizeWords(site.customer?.name) || "N/A"}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <UserCircleIcon className="h-5 w-5 mr-2" />
                            <span className="truncate max-w-[140px]">
                              {capitalizeWords(site.contactPerson) || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <PhoneIcon className="h-5 w-5 mr-2" />
                            {/* <span>{site.contactPhone || "N/A"}</span> */}
                            {site.contactPhone ? (
                              <a
                                href={`tel:${site.contactPhone}`}
                                className="hover:text-blue-600 truncate block max-w-[140px]"
                                title={site.contactPhone}
                              >
                                {site.contactPhone}
                              </a>
                            ) : (
                              "Not available"
                            )}
                          </div>
                        </div>


                        {/* <div className="space-y-3">
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <span className="pr-1">Total Orders: </span>
                            {site.totalOrder ? site.totalOrder : "0"}
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <span className="pr-1">Complete Orders </span>                          
                              {site.completedOrder ? site.completedOrder : "0"}
                            
                          </div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <span className="pr-1">Pending Orders </span>
                            {site.pendingOrder ? site.pendingOrder : "0"}
                          </div>
                        </div> */}
                        <div className="space-y-2">
                          <Typography variant="body2" fontWeight={300}> Orders </Typography>
                          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                            <span className="mr-1">Total:</span>
                            <span>{site.totalOrder ?? 0}</span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                            <span className="mr-1">Completed:</span>
                            <span>{site.completedOrder ?? 0}</span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                            <span className="mr-1">Pending:</span>
                            <span>{site.pendingOrder ?? 0}</span>
                          </div>
                        </div>
                      </div>

                    </div>
  
                    <div className="mt-3">
                      <Divider className="dark:border-gray-600" />
                      <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-600 h-12">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSite(site);
                        }}
                        className={`flex items-center justify-center px-4 py-1 text-sm transition-colors h-full hover:rounded-bl-2xl ${
                          !canEditSite || loading
                            ? "text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800 dark:text-gray-600"
                            : "text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10"
                        }`}
                        disabled={!canEditSite || loading}
                      >
                        Edit Site
                      </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            showCreateOrderPopup(site);
                          }}
                          disabled={!canCreateOrder || creatingOrderForSite === site.id || loading}
                          className={`flex items-center justify-center px-4 py-3 text-sm transition-colors h-full hover:rounded-br-2xl ${
                            !canCreateOrder || creatingOrderForSite === site.id || loading
                              ? "text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-800 dark:text-gray-600"
                              : "text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10"
                          }`}
                        >
                          {creatingOrderForSite === site.id ? "Creating..." : "Place Order"}
                        </button>
  
                      </div>
                    </div>
                  </div>      
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Orders Display Section */}
        {false && Object.keys(siteOrders).length > 0 && (
          <div className="mt-8">
            <Typography variant="h5" className="mb-4 font-semibold">
              Orders by Site
            </Typography>
            {Object.entries(siteOrders).map(([siteId, orders]) => {
              const site = sites.find(s => s.id === siteId);
              return (
                <Card key={siteId} className="mb-6">
                  <CardContent>
                    <Typography variant="h6" className="mb-4">
                      {site?.name} - {orders.length} Orders
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {orders.map((order) => (
                        <Card key={order.id} className="p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <Typography variant="subtitle1" fontWeight={600}>
                              {order.orderNumber}
                            </Typography>
                            <Chip
                              label={order.status}
                              color={order.status === 'completed' ? 'success' : 
                                     order.status === 'in_progress' ? 'primary' : 'default'}
                              size="small"
                            />
                          </div>
                          <Typography variant="body2" color="text.secondary" className="mb-3">
                            {order.customer?.name} • {dayjs(order.createdAt).format('MMM DD, YYYY')}
                          </Typography>
                          
                          {/* Products Section */}
                          {order.products && order.products.length > 0 && (
                            <div className="space-y-2">
                              <Typography variant="subtitle2" fontWeight={600}>
                                Products ({order.products.length})
                              </Typography>
                              {order.products.map((product) => (
                                <div key={product.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                                  <div>
                                    <Typography variant="body2" fontWeight={500}>
                                      {product.name || product.type}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {product.type} • {product.total_items || 0} items
                                    </Typography>
                                  </div>
                                  <IconButton
                                    size="small"
                                    onClick={async () => await handleProductSelect(order, product)}
                                    title="View Items & Stages"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </IconButton>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
        >
          <DialogTitle>{"Do you want to place order?"}</DialogTitle>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDialog(false)}>No</Button>
            <Button
              onClick={() => {
                console.log("Creating order for selected site:", selectedSite);
                console.log("Selected site ID:", selectedSite?.id);
                console.log(
                  "Selected site customerId:",
                  selectedSite?.customerId
                );
                console.log(
                  "Selected site customer object:",
                  selectedSite?.customer
                );
                showCreateOrderPopup(selectedSite);
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

      {/* {isCreateModalOpen &&  ( */}
      <SiteFormModel
        isOpen={isCreateModalOpen}
        // onClose={handleCloseCreateModal}
        // onClose={() => setCloseDialog(true)}
        onRequestClose={(hasData) => {
            if (hasData) {
              setCloseDialog(true);
            } else {
              setIsCreateModalOpen(false);
              setSelectedSite(null);
            }
          }}
        sites={selectedSite}
        onSuccess={handleSiteCreated}
      />
      {/* )} */}

      {DeleteDialog && (
        <ConfirmDialog
          open={DeleteDialog}
          onClose={() => setDeleteDialog(false)}
          onConfirm={() => {
            if (deleteSiteId) {
              const siteToDelete = sites.find(s => s.id === deleteSiteId);
              if (siteToDelete) handleDeleteSite(siteToDelete);
            }
            setDeleteDialog(false);
            setDeleteSiteId(null);
          }}
          title="Delete Site"
          message="Are you sure you want to delete this site?"
        />
      )}

      {closeDialog && (
        <CloseConfirmationDialogue
          open={closeDialog}
          onClose={() => setCloseDialog(false)}
          onConfirm={() => {
            setCloseDialog(false);
            setIsCreateModalOpen(false);
            setSelectedSite(null);
          }}
          title=""
          message="Are you sure you want to close this site model?"
        />
      )}


      {/* Removed CreateOrderPage modal since we're now navigating to a new page */}

      {/* Timeline Modal - Disabled, using side panels now */}
      {false && timelineData && (
        <UnifiedTimelineModal
          isOpen={showTimelineModal}
          onClose={() => {
            setShowTimelineModal(false);
            setTimelineData(null);
          }}
          data={timelineData}
        />
      )}

      {/* Order Side Panel */}
      <OrderSidePanel
        isOpen={showOrderSidePanel}
        onClose={closeOrderPanel}
        onCloseAll={closeAll}
        orders={siteOrders}
        onOrderSelect={handleOrderSelect}
        title={selectedSiteForOrders ? `${selectedSiteForOrders.name}` : undefined}
        loading={ordersLoading}
        onBackToSites={handleBackToSites}
        selectedOrderId={selectedOrderForProducts?.id}
        site={selectedSiteForOrders}
      />

      {/* Product Side Panel */}
      <ProductSidePanel
        isOpen={showProductSidePanel}
        onClose={closeProductPanel}
        onCloseAll={closeAll}
        products={orderProducts}
        onProductSelect={handleProductSelectFromPanel}
        title={selectedOrderForProducts ? `Order ${selectedOrderForProducts.orderNumberNew || selectedOrderForProducts.orderNumber || selectedOrderForProducts.order_tracking_code || selectedOrderForProducts.id} - Sets` : undefined}
        loading={productsLoading}
        onBackToOrders={handleBackToOrders}
        selectedProductId={selectedProduct?.id}
      />

      {/* Item & Stage Side Panel */}
      <ItemStageSidePanel
        isOpen={showItemSidePanel}
        onClose={closeItemPanel}
        onCloseAll={closeAll}
        product={selectedProduct}
        onItemSelect={handleItemSelect}
        onStageSelect={handleStageSelect}
        onBackToProducts={handleBackToProducts}
        title={selectedProduct ? `${selectedProduct.name || selectedProduct.type} - Parts` : undefined}
        order={selectedOrderForProducts || selectedOrder}
      />

      </div>      

      {/* Pagination */}
      {pagination?.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
        />        
      )}
    </PageWrapper>
      
    </>
  );
};
export default Sites;
